// AchievementsBlock.jsx
import { useState, useEffect, useRef } from 'react'
import Block from '../../../components/Block/Block'
import AchievementCard from '../../../components/AchievementCard/AchievementCard'
import AchievementModal from '../../../components/AchievementModal/AchievementModal'
import FadeIn from '../../../components/FadeIn/FadeIn'
import { fetchAchievements } from '../../../services/api'
import style from './achievementsblock.module.scss'

const AchievementsBlock = () => {
    const [achievements, setAchievements] = useState([])
    const [loading, setLoading] = useState(true)
    const [isMobile, setIsMobile] = useState(false)
    const [selectedAchievement, setSelectedAchievement] = useState(null)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [dragOffset, setDragOffset] = useState(0)
    const [dragProgress, setDragProgress] = useState(0)
    const [isAutoPlaying, setIsAutoPlaying] = useState(true)
    const [hovered, setHovered] = useState(false)
    
    const carouselRef = useRef(null)
    const containerRef = useRef(null)
    const autoPlayRef = useRef(null)

    const CARDS_TO_SHOW = 3

    useEffect(() => {
        const checkScreenSize = () => {
            const isMobileScreen = window.matchMedia('(max-width: 768px)').matches
            setIsMobile(isMobileScreen)
        }

        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)

        const loadAchievements = async () => {
            setLoading(true)
            try {
                const { data } = await fetchAchievements({
                    page: 1,
                    pageSize: 100
                });
                setAchievements(data)
                console.log(data)
            } catch (error) {
                console.error('Failed to load achievements:', error)
            } finally {
                setLoading(false)
            }
        }

        loadAchievements()
        return () => window.removeEventListener('resize', checkScreenSize)
    }, [])

    const handleAchievementClick = (achievement) => {
        setSelectedAchievement({
            id: achievement.id,
            title: achievement.attributes.Title,
            description: achievement.attributes.Description || achievement.attributes.Text || '',
            image: achievement.attributes.Image?.data?.attributes?.url ? 
                `${achievement.attributes.Image.data.attributes.url}` : 
                null,
            date: achievement.attributes.date || achievement.attributes.publishedAt || null,
            achievementType: achievement.attributes.type || 'achievement',
            medalType: achievement.attributes.medalType || null,
            competition: achievement.attributes.competition || null,
            year: achievement.attributes.year || null
        })
    }

    const handleCloseModal = () => {
        setSelectedAchievement(null)
    }

    const totalPages = Math.ceil(achievements.length / CARDS_TO_SHOW)
    
    // Auto-play logic
    useEffect(() => {
        if (isAutoPlaying && !isDragging && !hovered && achievements.length > 0) {
            autoPlayRef.current = setInterval(() => {
                setCurrentIndex((prev) => {
                    if (prev >= totalPages - 1) {
                        return 0
                    }
                    return prev + 1
                })
            }, 4000)
        }

        return () => {
            if (autoPlayRef.current) {
                clearInterval(autoPlayRef.current)
            }
        }
    }, [isAutoPlaying, isDragging, hovered, totalPages, achievements.length])

    const handleDragStart = (e) => {
        setIsDragging(true)
        setIsAutoPlaying(false)
        const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX
        setStartX(clientX)
        setDragOffset(0)
        if (carouselRef.current) {
            carouselRef.current.style.transition = 'none'
        }
    }

    const handleDragMove = (e) => {
        if (!isDragging) return
        e.preventDefault()
        
        const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX
        const deltaX = clientX - startX
        const containerWidth = containerRef.current?.offsetWidth || 0
        
        const progress = (deltaX / containerWidth) * 100
        setDragProgress(progress)
        
        if (carouselRef.current) {
            const currentTransform = -currentIndex * (100 / CARDS_TO_SHOW)
            carouselRef.current.style.transform = `translateX(calc(${currentTransform}% + ${deltaX}px))`
        }
    }

    const handleDragEnd = (e) => {
        if (!isDragging) return
        
        setIsDragging(false)
        
        const threshold = 50
        const deltaX = (e.type === 'mouseup' ? e.clientX : (e.changedTouches?.[0]?.clientX || 0)) - startX
        
        if (carouselRef.current) {
            carouselRef.current.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }
        
        let newIndex = currentIndex
        
        if (Math.abs(deltaX) > threshold) {
            if (deltaX > 0 && currentIndex > 0) {
                newIndex = currentIndex - 1
            } else if (deltaX < 0 && currentIndex < totalPages - 1) {
                newIndex = currentIndex + 1
            }
        }
        
        setCurrentIndex(newIndex)
        setDragProgress(0)
        
        setTimeout(() => {
            setIsAutoPlaying(true)
        }, 3000)
    }

    const goToNext = () => {
        if (currentIndex < totalPages - 1) {
            setCurrentIndex(currentIndex + 1)
            setIsAutoPlaying(false)
            setTimeout(() => setIsAutoPlaying(true), 5000)
        }
    }

    const goToPrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1)
            setIsAutoPlaying(false)
            setTimeout(() => setIsAutoPlaying(true), 5000)
        }
    }

    useEffect(() => {
        if (carouselRef.current && !isDragging) {
            const transformPercent = -currentIndex * (100 / CARDS_TO_SHOW)
            carouselRef.current.style.transform = `translateX(${transformPercent}%)`
        }
    }, [currentIndex, isDragging])

    const getVisibleAchievements = () => {
        const start = currentIndex * CARDS_TO_SHOW
        const end = start + CARDS_TO_SHOW
        return achievements.slice(start, end)
    }

    const visibleAchievements = getVisibleAchievements()

    if (loading) {
        return (
            <Block
                flexDirection='column'
                justifyContent='center'
                alignItems='start'
                width="75%"
                height='fit-content'
                background="rgba(255, 255, 255, 0.85)"
                header='НАШИ ДОСТИЖЕНИЯ'
            >
                <div className={style.loading}>Загрузка...</div>
            </Block>
        )
    }

    return (
        <>
            <Block
                flexDirection='column'
                justifyContent='center'
                alignItems='start'
                width="75%"
                height='fit-content'
                background="rgba(255, 255, 255, 0.85)"
                header='НАШИ ДОСТИЖЕНИЯ'
            >
                <FadeIn>
                    <div 
                        className={style.carouselContainer}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                    >
                        {/* Premium decorative elements */}
                        <div className={style.premiumGlow} />
                        <div className={style.premiumAccent} />
                        
                        {/* Auto-play indicator with red-blue gradient */}
                        <div className={style.autoPlayIndicator}>
                            <div className={style.autoPlayBar} />
                        </div>

                        {currentIndex > 0 && (
                            <button 
                                className={`${style.navButton} ${style.prevButton}`}
                                onClick={goToPrevious}
                                aria-label="Предыдущие достижения"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                            </button>
                        )}
                        
                        <div 
                            className={style.carouselWrapper}
                            ref={containerRef}
                            onMouseDown={handleDragStart}
                            onMouseMove={handleDragMove}
                            onMouseUp={handleDragEnd}
                            onMouseLeave={handleDragEnd}
                            onTouchStart={handleDragStart}
                            onTouchMove={handleDragMove}
                            onTouchEnd={handleDragEnd}
                        >
                            <div 
                                className={style.carouselTrack}
                                ref={carouselRef}
                                style={{
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                                }}
                            >
                                {achievements.map((item, idx) => (
                                    <div 
                                        key={item.id} 
                                        className={style.carouselItem}
                                        style={{
                                            width: `${100 / CARDS_TO_SHOW}%`,
                                            flexShrink: 0
                                        }}
                                    >
                                        <AchievementCard
                                            image={item.attributes.Image?.data?.attributes?.url ? 
                                                `${item.attributes.Image.data.attributes.url}` : 
                                                null}
                                            title={item.attributes.Title}
                                            onClick={() => handleAchievementClick(item)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {currentIndex < totalPages - 1 && (
                            <button 
                                className={`${style.navButton} ${style.nextButton}`}
                                onClick={goToNext}
                                aria-label="Следующие достижения"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </button>
                        )}
                        
                        <div className={style.dotsContainer}>
                            {Array.from({ length: totalPages }).map((_, idx) => (
                                <button
                                    key={idx}
                                    className={`${style.dot} ${currentIndex === idx ? style.activeDot : ''}`}
                                    onClick={() => {
                                        setCurrentIndex(idx)
                                        setIsAutoPlaying(false)
                                        setTimeout(() => setIsAutoPlaying(true), 5000)
                                    }}
                                    aria-label={`Перейти к странице ${idx + 1}`}
                                />
                            ))}
                        </div>
                        
                        <div className={style.controlsInfo}>
                            <span className={style.pageIndicator}>
                                {currentIndex + 1} / {totalPages}
                            </span>
                            <button 
                                className={style.playToggle}
                                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                                aria-label={isAutoPlaying ? 'Пауза' : 'Воспроизвести'}
                            >
                                {isAutoPlaying ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="6" y="4" width="4" height="16"/>
                                        <rect x="14" y="4" width="4" height="16"/>
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polygon points="5 3 19 12 5 21 5 3"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </FadeIn>
            </Block>

            <AchievementModal
                achievement={selectedAchievement}
                onClose={handleCloseModal}
            />
        </>
    )
}

export default AchievementsBlock